import {
  BackfillFeedType,
  BackfillPreviewItem,
  BackfillPreviewResult,
  CompanyContentHealth,
} from '../../types/contentCompletion';
import { LatestNewsDetail, RunSummary } from '../../api/scheduledJobs';
import type { BatchBackfillReviewItem } from '../ContentCompletionPage/BatchBackfillReviewDialog';

/** Build a minimal health row so existing backfill dialogs can resolve source URLs. */
export const companyFromLatestNewsDetail = (
  row: LatestNewsDetail
): CompanyContentHealth => {
  const source = (row.source_url || '').trim();
  const isRss = (row.feed_type || '').toLowerCase() === 'rss';
  return {
    company_id: row.company_id || '',
    company_name: row.company_name || row.company_id || '',
    corporate_status: row.corporate_status || undefined,
    press_release_url: isRss ? undefined : source || undefined,
    feed_news_rss: isRss ? source || undefined : undefined,
    has_feed_configured: Boolean(source),
    pr_total_count: 0,
    pr_recent_count: 0,
    quartr_total_count: 0,
    quartr_recent_count: 0,
    has_quartr_feed: false,
    status: 'stale',
    quartr_status: 'no_feed',
    last_pr_date: row.latest_press_release_date || undefined,
  };
};

export const feedTypeForLatestNews = (
  row: LatestNewsDetail
): BackfillFeedType => {
  const t = (row.feed_type || '').toLowerCase();
  if (t === 'rss') return 'rss';
  if (t === 'html') return 'html';
  return 'auto';
};

export const lookbackDaysForLatestNews = (
  row: LatestNewsDetail,
  fallback: number = 2
): number => {
  const days = row.lookback_days;
  if (typeof days === 'number' && days >= 1) {
    return Math.min(730, Math.max(1, days));
  }
  return Math.min(730, Math.max(1, fallback));
};

/** Convert stored agent items into a BackfillPreviewResult for review-only dialogs. */
export const previewFromLatestNewsDetail = (
  row: LatestNewsDetail,
  runId: string = `latest-news-${row.company_id || 'unknown'}`
): BackfillPreviewResult => {
  const items: BackfillPreviewItem[] = (row.items || [])
    .filter((item) => Boolean((item.url || '').trim()))
    .map((item) => ({
      title: item.title || undefined,
      published_date: item.published_date || undefined,
      url: item.url || undefined,
      has_full_text: Boolean(item.has_full_text),
      content_snippet: item.content_snippet || undefined,
    }));
  return {
    run_id: runId,
    status: 'success',
    company_id: row.company_id || '',
    company_name: row.company_name || row.company_id || '',
    source_url: (row.source_url || '').trim(),
    feed_type_used: row.feed_type || undefined,
    days_back: lookbackDaysForLatestNews(row),
    press_releases_found: row.press_releases_found ?? items.length,
    content_fetched: items.filter((i) => i.has_full_text).length,
    latest_press_release_date: row.latest_press_release_date || undefined,
    latest_press_release_title: row.latest_press_release_title || undefined,
    items,
  };
};

export const countFoundLatestNews = (summary?: RunSummary | null): number => {
  if (!summary?.details?.length) {
    return summary?.companies_with_news ?? 0;
  }
  return summary.details.filter(
    (row) =>
      row.status === 'found' &&
      (row.press_releases_found ?? row.items?.length ?? 0) > 0
  ).length;
};

/** Build batch-review items from a Latest News Agent run summary. */
export const buildLatestNewsReviewItems = (
  summary?: RunSummary | null
): { items: BatchBackfillReviewItem[]; daysBack: number; feedType: BackfillFeedType } => {
  const fallbackDays = summary?.lookback_days ?? 2;
  const ready: BatchBackfillReviewItem[] = [];
  let maxDays = fallbackDays;
  let feedType: BackfillFeedType = 'auto';

  for (const row of summary?.details || []) {
    if (row.status !== 'found') continue;
    const sourceUrl = (row.source_url || '').trim();
    if (!row.company_id || !sourceUrl) continue;
    const preview = previewFromLatestNewsDetail(row);
    if (preview.items.length === 0) continue;
    ready.push({
      companyId: row.company_id,
      companyName: row.company_name || row.company_id,
      company: companyFromLatestNewsDetail(row),
      sourceUrl,
      previewResult: preview,
    });
    maxDays = Math.max(maxDays, lookbackDaysForLatestNews(row, fallbackDays));
    if (ready.length === 1) {
      feedType = feedTypeForLatestNews(row);
    } else if (feedType !== feedTypeForLatestNews(row)) {
      feedType = 'auto';
    }
  }

  return { items: ready, daysBack: maxDays, feedType };
};
